/**
 * The lamps, switches and cords derived by `buildFixtures`.
 *
 * The switch is the only thing here with a collider, and it needs one: the crosshair
 * finds targets by casting a ray at the physics world and deliberately ignores sensors
 * (see `useInteractionTarget`), so an intangible switch would be unclickable. A solid
 * plate is harmless — it stands 3.5 cm off a wall the player's capsule already keeps
 * them a third of a metre away from, and it is never in a doorway.
 *
 * Cords are instanced together; there are three per room and they never change. The
 * fittings are individual meshes because each one dims with its own room.
 */

import { Instance, Instances } from '@react-three/drei';
import { CuboidCollider, RigidBody } from '@react-three/rapier';
import type { BuiltFixtures } from '../game/profile/buildFixtures';
import type { LightState } from '../game/interaction';
import { boxPosition, halfExtents } from './boxUnits';
import { getGradientMap } from './materials';
import type { BodyUserData } from './useInteractionTarget';

type Props = {
  fixtures: BuiltFixtures;
  roomLights: Readonly<Record<string, LightState>>;
  trimColor: string;
};

/** A lit shade glows; a dark one is just a shape. */
const SHADE_LIT = '#ffe6b8';
const SHADE_DARK = '#6b6355';

export function RoomFixtures({ fixtures, roomLights, trimColor }: Props) {
  const { switches, fittings, cords } = fixtures;

  return (
    <>
      {cords.length > 0 && (
        <Instances limit={cords.length} range={cords.length} receiveShadow>
          <boxGeometry args={[1, 1, 1]} />
          <meshToonMaterial color="#3a352e" gradientMap={getGradientMap()} />
          {cords.map((cord, index) => (
            <Instance
              key={`cord-${cord.roomId}-${index}`}
              position={boxPosition(cord.box)}
              scale={[cord.box.size.x, cord.box.size.y, cord.box.size.z]}
            />
          ))}
        </Instances>
      )}

      {fittings.map((fitting) => {
        const lit = (roomLights[fitting.roomId] ?? 'on') === 'on';
        return (
          <mesh key={`fitting-${fitting.roomId}`} position={boxPosition(fitting.box)}>
            <boxGeometry
              args={[fitting.box.size.x, fitting.box.size.y, fitting.box.size.z]}
            />
            {/* Unlit material: a shade with a bulb in it should not itself be shaded. */}
            <meshBasicMaterial color={lit ? SHADE_LIT : SHADE_DARK} />
          </mesh>
        );
      })}

      {switches.map((plate) => (
        <RigidBody
          key={`switch-${plate.roomId}`}
          type="fixed"
          colliders={false}
          userData={{ kind: 'light-switch', id: plate.roomId } satisfies BodyUserData}
        >
          <CuboidCollider args={halfExtents(plate.box)} position={boxPosition(plate.box)} />
          <mesh position={boxPosition(plate.box)} receiveShadow>
            <boxGeometry args={[plate.box.size.x, plate.box.size.y, plate.box.size.z]} />
            <meshToonMaterial color={trimColor} gradientMap={getGradientMap()} />
          </mesh>
        </RigidBody>
      ))}
    </>
  );
}
